import React from 'react';

// Pagination component for data lists
// Props:
//   currentPage: number - current page (1-indexed)
//   totalItems: number - total number of items
//   itemsPerPage: number - items per page
//   onPageChange: (page) => void - called when page changes
//   onItemsPerPageChange: (itemsPerPage) => void - called when items per page changes
//   itemsPerPageOptions: number[] - available options for items per page (default: [10, 25, 50, 100])

function Pagination({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100]
}) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and pages around current
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        Showing {startItem}-{endItem} of {totalItems} items
      </div>

      <div className="pagination-controls">
        <div className="items-per-page">
          <label htmlFor="itemsPerPage">Show:</label>
          <select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          >
            {itemsPerPageOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div className="page-navigation">
          <button
            className="page-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>

          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
            ) : (
              <button
                key={page}
                className={`page-btn page-number ${currentPage === page ? 'active' : ''}`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            )
          ))}

          <button
            className="page-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      </div>

      <style>{`
        .pagination-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: var(--card-bg, #ffffff);
          border-top: 1px solid var(--border-color, #e0e0e0);
          margin-top: 16px;
        }

        .pagination-info {
          color: var(--text-secondary, #666);
          font-size: 14px;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 24px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .items-per-page {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .items-per-page label {
          color: var(--text-secondary, #666);
          font-size: 14px;
        }

        .items-per-page select {
          padding: 6px 12px;
          border: 1px solid var(--border-color, #ddd);
          border-radius: 4px;
          background: var(--input-bg, #fff);
          color: var(--text-primary, #333);
          font-size: 14px;
          cursor: pointer;
        }

        .page-navigation {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .page-btn {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #ddd);
          background: var(--button-bg, #fff);
          color: var(--text-primary, #333);
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .page-btn:hover:not(:disabled) {
          background: var(--button-hover-bg, #f0f0f0);
          border-color: var(--primary-color, #3b82f6);
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-btn.active {
          background: var(--primary-color, #3b82f6);
          border-color: var(--primary-color, #3b82f6);
          color: white;
        }

        .page-number {
          min-width: 40px;
          text-align: center;
        }

        .page-ellipsis {
          padding: 8px 4px;
          color: var(--text-secondary, #666);
        }

        @media (max-width: 600px) {
          .pagination-container {
            padding: 12px;
          }

          .pagination-controls {
            gap: 12px;
          }

          .page-btn {
            padding: 6px 10px;
            font-size: 13px;
          }

          .page-number {
            min-width: 36px;
          }

          /* Hide page numbers on very small screens, keep prev/next */
          .page-number {
            display: none;
          }

          .page-ellipsis {
            display: none;
          }
        }

        @media (min-width: 601px) and (max-width: 768px) {
          .page-number {
            display: inline-block;
          }
        }
      `}</style>
    </div>
  );
}

export default Pagination;
